import { lazy, Suspense } from 'react';
import { Links, Meta, Outlet, Scripts, ScrollRestoration, useLocation, useRouteLoaderData } from 'react-router';

import type { Route } from './+types/root';
import type { PublicEnv } from './config.server';

import './app.css';

import { isPreviewRequest } from './cms/client.server';
import { metaFor } from './cms/meta';
import { prismicToolbarSrc } from './cms/preview.server';
import { siteSettingsFallback } from './cms/site-settings';
import { getSiteSettings } from './cms/site-settings.server';
import { GeneralErrorBoundary } from './components/error-boundary';
import { PrismicPreview } from './components/prismic-preview';
import { ProgressBar } from './components/progress-bar';
import { config, getPublicEnv, isProduction } from './config.server';
import { getLanguage, getLanguages, languageCodeFromRequest } from './i18n/languages.server';
import { useNonce } from './providers/nonce-provider';
import { KIDS_THEME, themeClassName, themeFromPathname } from './utils/theme';

/**
 * The non-production QA widget (ticket 13). `__QA_WIDGET_ENABLED__` is replaced
 * at build time (see `vite.config.ts`), so a production build has `null` here
 * and Rollup never emits the widget's chunk; the runtime check below gates on
 * the public `ENVIRONMENT_TYPE` as well, for any environment that ships the
 * chunk but should not show the control.
 */
const QaWidget = __QA_WIDGET_ENABLED__ ? lazy(() => import('./components/qa/qa-widget')) : null;

export const links: Route.LinksFunction = () => [
  // No sprite preload. `<Icon>` reaches the sheet through `<use href>`, whose
  // request destination is not `image` — so `as: 'image'` never matched the
  // real fetch, Chrome warned that the preload went unused, and the browser
  // downloaded the sheet twice. The first icon on screen requests it in the
  // same round trip anyway, and `<use>` does not block render.
  // The PNG icon is rendered in `Document` instead, because it comes from
  // `site_settings.favicon` and `links` cannot read loader data.
  // The two faces the first paint needs. Preloaded rather than discovered from
  // the stylesheet, so the metric-matched fallbacks in `app.css` are on screen
  // for as few frames as possible.
  {
    rel: 'preload',
    href: '/fonts/bebas-neue-latin.woff2',
    as: 'font',
    type: 'font/woff2',
    crossOrigin: 'anonymous',
  } as const,
  {
    rel: 'preload',
    href: '/fonts/roboto-latin.woff2',
    as: 'font',
    type: 'font/woff2',
    crossOrigin: 'anonymous',
  } as const,
  { rel: 'icon', href: '/favicon.ico', sizes: 'any' },
  { rel: 'apple-touch-icon', href: '/static/favicons/apple-touch-icon.png' },
  {
    rel: 'manifest',
    href: '/site.webmanifest',
    crossOrigin: 'use-credentials',
  } as const, // the literal type is needed to satisfy the DOM typings
];

/**
 * Site-wide SEO defaults from `site_settings`. A route overrides them by
 * exporting its own `meta` built with the same `metaFor` helper, which is where
 * a per-route canonical URL comes from.
 */
export const meta: Route.MetaFunction = ({ data }) =>
  data ? metaFor({ siteSettings: data.siteSettings }) : [{ title: 'Beach Passport' }];

export async function loader({ request }: Route.LoaderArgs) {
  const code = languageCodeFromRequest(request);

  const [{ option, strings }, languages, siteSettings] = await Promise.all([
    getLanguage(code),
    getLanguages(),
    getSiteSettings(),
  ]);

  return {
    ENV: getPublicEnv(),
    /** Active language code — `en` until the learner picks another. */
    language: option.code,
    direction: option.direction,
    /** English catalogue defaults with the active `language` document merged over them. */
    strings,
    /** The ordered language picker list. */
    languages,
    siteSettings,
    gtmId: config.secrets.APP_GTM_ID,
    allowIndexing: isProduction,

    // --- Prismic preview (ticket 06) ---------------------------------------
    // Resolved here rather than in the component because `preview.server` must
    // not reach the client bundle. `null` outside a preview session.
    prismicToolbarSrc: isPreviewRequest(request) ? prismicToolbarSrc : null,
  };
}

type RootLoaderData = Awaited<ReturnType<typeof loader>>;

/**
 * Bootstraps `window.dataLayer` and loads Google Tag Manager. The inline script
 * carries the CSP nonce, and GTM's own injected script is permitted by
 * `strict-dynamic` because this script creates it.
 */
function GoogleTagManager({ gtmId, nonce }: { gtmId: string; nonce: string }) {
  return (
    <script
      nonce={nonce}
      dangerouslySetInnerHTML={{
        __html: `window.dataLayer=window.dataLayer||[];window.dataLayer.push({'gtm.start':new Date().getTime(),event:'gtm.js'});
(function(){var s=document.createElement('script');s.async=true;s.src='https://www.googletagmanager.com/gtm.js?id=${gtmId}';s.setAttribute('nonce','${nonce}');document.head.appendChild(s);})();`,
      }}
    />
  );
}

/**
 * The app's single document. Both `App` and `ErrorBoundary` render it, so the
 * theme class, language and analytics live here rather than in a layout.
 */
function Document({
  children,
  nonce,
  env,
  language = 'en',
  direction = 'ltr',
  faviconUrl = siteSettingsFallback.faviconUrl,
  gtmId,
  allowIndexing = true,
  prismicToolbarSrc = null,
}: {
  children: React.ReactNode;
  nonce: string;
  env?: PublicEnv;
  language?: RootLoaderData['language'];
  direction?: RootLoaderData['direction'];
  faviconUrl?: string;
  gtmId?: string;
  allowIndexing?: boolean;
  prismicToolbarSrc?: string | null;
}) {
  const theme = themeFromPathname(useLocation().pathname);

  return (
    <html lang={language} dir={direction} className={themeClassName(theme)}>
      <head>
        <Meta />
        <meta charSet="utf-8" />
        {/*
          `viewport-fit=cover` lets the page paint under the display cutout and
          the home indicator, which is what makes a standalone install look
          native. Every fixed edge puts the inset back with the `safe-*`
          utilities in `app.css`. `maximum-scale` is deliberately absent: the
          reader must be able to zoom. `interactive-widget=resizes-content` keeps a
          bottom-anchored primary action above the on-screen keyboard.
        */}
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1,viewport-fit=cover,interactive-widget=resizes-content"
        />
        {/*
          The browser chrome and the standalone status bar take their colour
          from the page's own ground, so the app reads as one surface rather
          than a site inside a bar. Kids get the brighter blue their theme uses.
        */}
        <meta name="theme-color" content={theme === KIDS_THEME ? '#0032c7' : '#0e2981'} />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
        <meta name="apple-mobile-web-app-title" content="Beach Passport" />
        {allowIndexing ? null : <meta name="robots" content="noindex, nofollow" />}
        <link rel="icon" type="image/png" sizes="96x96" href={faviconUrl} />
        <Links />
      </head>

      <body className="bg-background text-foreground relative isolate flex min-h-svh flex-col font-sans antialiased">
        {children}
        <script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(env ?? {})}`,
          }}
        />
        {gtmId ? <GoogleTagManager gtmId={gtmId} nonce={nonce} /> : null}
        {prismicToolbarSrc ? <PrismicPreview src={prismicToolbarSrc} nonce={nonce} /> : null}
        <ScrollRestoration nonce={nonce} />
        <Scripts nonce={nonce} />
      </body>
    </html>
  );
}

export default function App() {
  // `useRouteLoaderData` rather than `useLoaderData` so the same hook shape
  // works if this document is ever rendered from a child boundary.
  const data = useRouteLoaderData<typeof loader>('root');
  const nonce = useNonce();

  return (
    <Document
      nonce={nonce}
      env={data?.ENV}
      language={data?.language}
      direction={data?.direction}
      faviconUrl={data?.siteSettings.faviconUrl}
      gtmId={data?.gtmId}
      allowIndexing={data?.allowIndexing ?? false}
      prismicToolbarSrc={data?.prismicToolbarSrc ?? null}
    >
      <Outlet />
      <ProgressBar />
      {QaWidget && data?.ENV.ENVIRONMENT_TYPE !== 'Production' ? (
        <Suspense fallback={null}>
          <QaWidget />
        </Suspense>
      ) : null}
    </Document>
  );
}

export function ErrorBoundary() {
  const nonce = useNonce();

  // NOTE: the root loader may not have run, so there is no ENV, language or GTM
  // id to hand the document here.
  return (
    <Document nonce={nonce} allowIndexing={false}>
      <GeneralErrorBoundary />
    </Document>
  );
}
